import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { Play, Pause, RotateCcw, ChevronsLeft, ChevronsRight, Gauge, Maximize2, Minimize2, ArrowLeftRight, Type, MoveHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { buildReadingModel } from "@/components/reader/readingModel";
import { cn } from "@/lib/utils";

const PREFS_KEY = "reading_os_prefs";
const loadPrefs = () => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; }
    catch (e) { return {}; }
};
const savePref = (patch) => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...patch })); } catch (e) {}
};

const CHUNK_SIZES = { short: 6, medium: 12, long: 24 };

// Build the moving text stream as a sequence of chunks. Each chunk slides
// across the fixed reading band, then the next one enters.
const buildChunks = (text, preset) => {
    const allWords = (text || "").split(/\s+/).filter(Boolean);
    const totalWords = Math.max(1, allWords.length);
    const chunks = [];
    if (preset === "sentence") {
        const { sentences } = buildReadingModel(text);
        let cum = 0;
        sentences.forEach((s) => {
            const w = s.text.split(/\s+/).filter(Boolean);
            if (w.length <= 40) {
                chunks.push({ text: s.text, wordCount: Math.max(1, w.length), wordStart: cum });
                cum += w.length;
            } else {
                for (let i = 0; i < w.length; i += 40) {
                    const piece = w.slice(i, i + 40);
                    chunks.push({ text: piece.join(" "), wordCount: piece.length, wordStart: cum });
                    cum += piece.length;
                }
            }
        });
    } else {
        const size = CHUNK_SIZES[preset] || CHUNK_SIZES.medium;
        for (let i = 0; i < allWords.length; i += size) {
            const piece = allWords.slice(i, i + size);
            chunks.push({ text: piece.join(" "), wordCount: piece.length, wordStart: i });
        }
    }
    if (!chunks.length) chunks.push({ text: "End of document", wordCount: 1, wordStart: 0 });
    return { chunks, totalWords };
};

/**
 * Ticker Mode — a true moving-text reading style. The reading band stays fixed
 * while the text streams continuously across it (news-ticker / teleprompter
 * style), one sentence/chunk at a time. The reader keeps their eyes in one
 * place; the words come to them. Direction, speed, font size and chunk length
 * are all adjustable.
 */
export const TickerStage = ({
    text = "",
    total = 0,
    initialIndex = 0,
    seekTo,
    onProgress,
    onSession,
    onComplete,
}) => {
    const [chunkPreset, setChunkPreset] = useState(() => loadPrefs().tickerChunk || "sentence");
    const { chunks, totalWords } = useMemo(() => buildChunks(text, chunkPreset), [text, chunkPreset]);
    const ratio = total > 0 ? total / totalWords : 1;

    const initialChunk = useMemo(() => {
        const target = (total > 0 ? initialIndex / total : 0) * totalWords;
        let idx = 0;
        for (let i = 0; i < chunks.length; i++) { if (chunks[i].wordStart <= target) idx = i; else break; }
        return idx;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chunks]);

    const [current, setCurrent] = useState(initialChunk);
    const [playing, setPlaying] = useState(false);
    const [wpm, setWpm] = useState(() => loadPrefs().wpm || 350);
    const [fontSize, setFontSize] = useState(() => loadPrefs().tickerFontSize || 52);
    const [direction, setDirection] = useState(() => loadPrefs().tickerDirection || "rtl"); // rtl | ltr
    const [focus, setFocus] = useState(false);

    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const posRef = useRef(0);
    const metricsRef = useRef({ pxPerSec: 60, endPos: 0, dir: -1 });
    const readyRef = useRef(false);
    const rafRef = useRef(null);
    const lastTsRef = useRef(null);
    const playingRef = useRef(false);
    const currentRef = useRef(current);
    const sessionRef = useRef({ startTime: null, startChunk: 0 });

    useEffect(() => { currentRef.current = current; }, [current]);
    useEffect(() => { playingRef.current = playing; }, [playing]);

    // Persist settings.
    useEffect(() => { savePref({ wpm }); }, [wpm]);
    useEffect(() => { savePref({ tickerFontSize: fontSize }); }, [fontSize]);
    useEffect(() => { savePref({ tickerDirection: direction }); }, [direction]);
    useEffect(() => { savePref({ tickerChunk: chunkPreset }); }, [chunkPreset]);

    const applyTransform = () => {
        if (trackRef.current) trackRef.current.style.transform = `translateX(${posRef.current}px)`;
    };

    // Measure the current chunk + (re)position whenever it changes or a visual
    // setting changes. While playing, a freshly-loaded chunk enters from the
    // edge; while paused it sits centered so the reader can see it.
    useLayoutEffect(() => {
        const el = trackRef.current, c = containerRef.current;
        if (!el || !c) return;
        const textWidth = el.offsetWidth;
        const cw = c.clientWidth;
        const chunk = chunks[current];
        const words = Math.max(1, chunk?.wordCount || 1);
        const chunkSeconds = Math.max(0.7, (words / Math.max(60, wpm)) * 60);
        const pxPerSec = Math.max(20, textWidth / chunkSeconds);
        const dir = direction === "rtl" ? -1 : 1;
        const startPos = dir < 0 ? cw : -textWidth;
        const endPos = dir < 0 ? -textWidth : cw;
        metricsRef.current = { pxPerSec, endPos, dir, cw, textWidth };
        posRef.current = playingRef.current ? startPos : Math.round((cw - textWidth) / 2);
        applyTransform();
        readyRef.current = true;
    }, [current, fontSize, direction, wpm, chunks]);

    // Report progress when the active chunk changes.
    useEffect(() => {
        const chunk = chunks[current];
        if (!chunk) return;
        const idx = Math.round(chunk.wordStart * ratio);
        const pct = total ? (idx / total) * 100 : 0;
        onProgress?.(idx, pct);
    }, [current, chunks, ratio, total, onProgress]);

    const flushSession = useCallback(() => {
        const s = sessionRef.current;
        if (s.startTime == null) return;
        const dur = (Date.now() - s.startTime) / 1000;
        const startWord = chunks[s.startChunk]?.wordStart ?? 0;
        const curWord = chunks[currentRef.current]?.wordStart ?? startWord;
        const wr = Math.max(0, Math.round((curWord - startWord) * ratio));
        s.startTime = null;
        if (dur > 1 && wr > 0) onSession?.({ wordsRead: wr, durationSec: dur, wpm, mode: "ticker" });
    }, [chunks, ratio, onSession, wpm]);

    // Animation loop.
    useEffect(() => {
        if (!playing) return;
        sessionRef.current = { startTime: Date.now(), startChunk: currentRef.current };
        lastTsRef.current = null;
        const step = (ts) => {
            if (lastTsRef.current == null) lastTsRef.current = ts;
            const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
            lastTsRef.current = ts;
            const m = metricsRef.current;
            if (readyRef.current) {
                posRef.current += m.dir * m.pxPerSec * dt;
                applyTransform();
                const passed = m.dir < 0 ? posRef.current <= m.endPos : posRef.current >= m.endPos;
                if (passed) {
                    readyRef.current = false;
                    const ni = currentRef.current + 1;
                    if (ni >= chunks.length) {
                        setPlaying(false);
                        onComplete?.();
                        return;
                    }
                    setCurrent(ni);
                }
            }
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            flushSession();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing, chunks]);

    useEffect(() => () => flushSession(), [flushSession]);

    // External seek (chat jump-to-source).
    useEffect(() => {
        if (!seekTo || typeof seekTo.index !== "number" || !chunks.length) return;
        setPlaying(false);
        const target = (total > 0 ? seekTo.index / total : 0) * totalWords;
        let idx = 0;
        for (let i = 0; i < chunks.length; i++) { if (chunks[i].wordStart <= target) idx = i; else break; }
        setCurrent(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seekTo?.ts]);

    // Re-center on resize when paused.
    useEffect(() => {
        const onResize = () => {
            if (playingRef.current) return;
            const el = trackRef.current, c = containerRef.current;
            if (!el || !c) return;
            posRef.current = Math.round((c.clientWidth - el.offsetWidth) / 2);
            applyTransform();
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const togglePlay = useCallback(() => {
        setCurrent((c) => (c >= chunks.length - 1 ? 0 : c));
        setPlaying((p) => !p);
    }, [chunks.length]);
    const prev = useCallback(() => { setPlaying(false); setCurrent((c) => Math.max(0, c - 1)); }, []);
    const next = useCallback(() => { setPlaying(false); setCurrent((c) => Math.min(chunks.length - 1, c + 1)); }, [chunks.length]);
    const restart = useCallback(() => { setPlaying(false); setCurrent(0); }, []);
    const flipDirection = useCallback(() => setDirection((d) => (d === "rtl" ? "ltr" : "rtl")), []);

    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            if (e.code === "Space") { e.preventDefault(); togglePlay(); }
            else if (e.code === "ArrowLeft") { e.preventDefault(); prev(); }
            else if (e.code === "ArrowRight") { e.preventDefault(); next(); }
            else if (e.key === "f" || e.key === "F") { setFocus((v) => !v); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [togglePlay, prev, next]);

    const pct = chunks.length ? Math.round(((current + 1) / chunks.length) * 100) : 0;

    return (
        <div className={cn(focus ? "fixed inset-0 z-50 bg-surface-0 p-3 sm:p-6" : "")} data-testid="ticker-stage">
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-surface-1 noise-overlay">
                <div className="reader-vignette pointer-events-none absolute inset-0" />

                {/* Top meta */}
                <div className="relative z-10 flex items-center justify-between px-4 pt-4">
                    <div className="flex items-center gap-2 mono-label text-t4"><MoveHorizontal className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Ticker reading</div>
                    <Button data-testid="ticker-focus-toggle" variant="ghost" size="icon" className="text-t3" onClick={() => setFocus((v) => !v)}>
                        {focus ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                </div>

                {/* Fixed reading band — text moves across this. */}
                <div className="relative flex flex-1 items-center" style={{ minHeight: focus ? "auto" : 280 }}>
                    {/* fixation guide line */}
                    <div className="pointer-events-none absolute left-1/2 top-6 bottom-6 z-10 w-px -translate-x-1/2 bg-[hsl(var(--primary))]/25" />
                    {/* edge fades so words appear/disappear softly */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-surface-1 to-transparent sm:w-28" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-surface-1 to-transparent sm:w-28" />
                    <div ref={containerRef} className="relative w-full overflow-hidden" data-testid="ticker-band">
                        <div
                            ref={trackRef}
                            data-testid="ticker-track"
                            className="inline-block whitespace-nowrap px-2 font-sans-display font-semibold tracking-tight text-t1 will-change-transform"
                            style={{ fontSize: `${fontSize}px`, lineHeight: 1.3 }}
                        >
                            {chunks[current]?.text || ""}
                        </div>
                    </div>
                </div>

                {/* Progress */}
                <div className="relative z-10 px-5">
                    <div className="h-1 w-full overflow-hidden rounded-full bg-surface-3">
                        <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between font-mono-ui text-[11px] text-t4">
                        <span>{pct}%</span>
                        <span>chunk {Math.min(current + 1, chunks.length)} / {chunks.length}</span>
                        <span>{direction === "rtl" ? "← left" : "right →"}</span>
                    </div>
                </div>

                {/* Controls dock */}
                <div className="relative z-10 mt-3 border-t border-border bg-surface-1/70 px-4 py-3 backdrop-blur-md">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5">
                            <Button data-testid="ticker-restart-button" variant="ghost" size="icon" className="text-t3" onClick={restart}><RotateCcw className="h-4 w-4" /></Button>
                            <Button data-testid="ticker-prev-button" variant="ghost" size="icon" className="text-t2" onClick={prev}><ChevronsLeft className="h-5 w-5" /></Button>
                            <Button data-testid="ticker-play-pause-button" size="icon" className="h-11 w-11 rounded-full" onClick={togglePlay}>
                                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                            </Button>
                            <Button data-testid="ticker-next-button" variant="ghost" size="icon" className="text-t2" onClick={next}><ChevronsRight className="h-5 w-5" /></Button>
                        </div>
                        <div className="flex items-center gap-3">
                            <Gauge className="h-4 w-4 text-t4" />
                            <div className="w-28"><Slider data-testid="ticker-wpm-slider" value={[wpm]} min={100} max={1500} step={25} onValueChange={(v) => setWpm(v[0])} /></div>
                            <span className="w-16 font-mono-ui text-xs text-t2">{wpm} wpm</span>
                        </div>
                    </div>

                    {/* Settings: font size · chunk length · direction */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2 rounded-md bg-surface-2 px-2.5 py-1.5">
                            <Type className="h-3.5 w-3.5 text-t4" />
                            <div className="w-20"><Slider data-testid="ticker-fontsize-slider" value={[fontSize]} min={28} max={88} step={4} onValueChange={(v) => setFontSize(v[0])} /></div>
                            <span className="w-8 font-mono-ui text-[11px] text-t3">{fontSize}</span>
                        </div>

                        <Select value={chunkPreset} onValueChange={setChunkPreset}>
                            <SelectTrigger data-testid="ticker-chunk-select" className="h-8 w-[130px] bg-surface-2 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="short">Short (6 words)</SelectItem>
                                <SelectItem value="medium">Medium (12 words)</SelectItem>
                                <SelectItem value="sentence">Full sentence</SelectItem>
                                <SelectItem value="long">Long (24 words)</SelectItem>
                            </SelectContent>
                        </Select>

                        <Button data-testid="ticker-direction-toggle" variant="secondary" size="sm" className="ml-auto h-8 gap-1.5 bg-surface-2 text-xs" onClick={flipDirection}>
                            <ArrowLeftRight className="h-3.5 w-3.5" />
                            {direction === "rtl" ? "Right → Left" : "Left → Right"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
